/**
 * timeGrid — where an event sits in an hour grid, and how wide it is
 * when something else is there at the same time.
 *
 * CAL-PRO-1. The day view positioned events by start and duration and
 * stopped there, so two shoots at eleven were drawn on top of each
 * other — the first thing that tells you a calendar is not a real one.
 * Every professional calendar splits the column instead, and that
 * split is the only genuinely non-obvious piece of a time grid, so it
 * lives here on its own: pure functions, no React, no dates beyond
 * what arithmetic needs.
 *
 * The same layout serves one day and seven. A week view is seven of
 * these columns side by side, which is why this takes a day and not a
 * range: no view has to know how the other one works.
 *
 * VERIFIED. `timeGrid.spec.ts` beside this file is 36 assertions over
 * the cases that are easy to get wrong — transitive clusters, column
 * reuse, widening, midnight clipping, zero-length events. Run it with
 *
 *     npx tsx src/components/calendar/timeGrid.spec.ts
 *
 * It is a script rather than a framework file because this repo has no
 * test runner; a vitest file here would be a file that never runs.
 */

/** The least an event needs for the grid to place it. */
export interface GridInput {
  id: string;
  start: Date;
  end: Date;
  allDay?: boolean;
}

export interface Placed<T extends GridInput> {
  event: T;
  /** Percent down the column, 0-100. */
  top: number;
  /** Percent of the column's height. Never below `MIN_HEIGHT_PCT`. */
  height: number;
  /** Percent across the column, 0-100. */
  left: number;
  /** Percent of the column's width. */
  width: number;
  /** How many columns this event's cluster needed — chrome can thin out. */
  crowd: number;
  /** The event runs past the top or bottom of this day. */
  clippedStart: boolean;
  clippedEnd: boolean;
}

export interface DayLayout<T extends GridInput> {
  /** Positioned events, in paint order (widest and leftmost first). */
  timed: Placed<T>[];
  /** All-day and multi-day events, for the banner above the grid. */
  allDay: T[];
}

const MINUTES = 24 * 60;
/**
 * A fifteen-minute meeting is three pixels of a 1200px day, which is
 * not a thing anybody can read or click. Every calendar has a floor;
 * this one is about twenty minutes.
 */
const MIN_HEIGHT_PCT = (20 / MINUTES) * 100;

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
};

/** Minutes from midnight, clamped into the day. */
const minutesInto = (at: Date, dayStart: Date) =>
  Math.max(0, Math.min(MINUTES, (at.getTime() - dayStart.getTime()) / 60000));

/**
 * Does this event show on this day at all?
 *
 * A booking that runs 23:00-01:00 belongs to BOTH days, clipped at
 * each end — which is why clipping happens per day rather than once
 * per event.
 */
export function touchesDay(e: GridInput, day: Date): boolean {
  const from = startOfDay(day);
  const to = addDays(from, 1);
  // CAL-PRO-4 — a zero-length event belongs to the day it falls on.
  //
  // `end > from` is right for anything that spans time and WRONG for
  // anything stamped at a moment: a project due date and a milestone
  // are both `allDay` with the end equal to the start, both at
  // midnight, so `midnight > midnight` is false and each one was
  // excluded from its own day. They showed in the month grid, which
  // is other code, and had been quietly missing from the week and the
  // day views since those were built.
  if (e.end.getTime() <= e.start.getTime()) return e.start >= from && e.start < to;
  return e.start < to && e.end > from;
}

/** Spans a whole day or more: the banner, not the grid. */
function isBanner(e: GridInput, dayStart: Date): boolean {
  if (e.allDay) return true;
  const dayEnd = addDays(dayStart, 1);
  return e.start <= dayStart && e.end >= dayEnd;
}

/**
 * Lay one day out.
 *
 * The column split is the standard sweep every calendar uses, and two
 * parts are worth stating because they are what a naive version gets
 * wrong:
 *
 * 1. Events are clustered by TRANSITIVE overlap. A 9-10, a 9:30-10:30
 *    and a 10-11 are one cluster even though the first and last never
 *    touch — split pairwise, the middle one would have to be in two
 *    places at once.
 *
 * 2. A column is REUSED the moment it frees up. In that same example
 *    the 10-11 takes the column the 9-10 has finished with, so the
 *    morning is two columns wide, not three. Without it a busy day
 *    gets thinner and thinner across the morning.
 *
 * Then each event widens to the right across any column that is free
 * for its WHOLE span — which is why a lone event after a busy hour
 * gets the full width back instead of keeping a third of it.
 */
export function layoutDay<T extends GridInput>(events: T[], day: Date): DayLayout<T> {
  const dayStart = startOfDay(day);
  const here = events.filter((e) => touchesDay(e, day));

  const allDay = here.filter((e) => isBanner(e, dayStart));
  const timedRaw = here.filter((e) => !isBanner(e, dayStart));

  // Earliest first; the longer of two equal starts goes left, which
  // keeps a long event from being boxed in by a short one on top of it.
  const sorted = [...timedRaw].sort((a, b) => {
    const d = a.start.getTime() - b.start.getTime();
    if (d !== 0) return d;
    return b.end.getTime() - b.start.getTime() - (a.end.getTime() - a.start.getTime());
  });

  interface Span {
    event: T;
    from: number;
    to: number;
    col: number;
    clippedStart: boolean;
    clippedEnd: boolean;
  }

  const spans: Span[] = sorted.map((event) => {
    const from = minutesInto(event.start, dayStart);
    const rawTo = minutesInto(event.end, dayStart);
    return {
      event,
      from,
      // A zero-length event still has to be visible and still has to
      // collide with its neighbours, so it gets the floor here rather
      // than only at paint time.
      to: Math.max(rawTo, from + 20),
      col: 0,
      clippedStart: event.start < dayStart,
      clippedEnd: event.end > addDays(dayStart, 1),
    };
  });

  const placed: Placed<T>[] = [];
  let i = 0;
  while (i < spans.length) {
    // One cluster: keep taking events while any of them still reaches
    // past the latest end seen so far.
    let clusterEnd = spans[i].to;
    let j = i + 1;
    while (j < spans.length && spans[j].from < clusterEnd) {
      clusterEnd = Math.max(clusterEnd, spans[j].to);
      j += 1;
    }
    const cluster = spans.slice(i, j);

    // Greedy columns: the first one whose occupant has finished.
    const colEnds: number[] = [];
    for (const s of cluster) {
      let col = colEnds.findIndex((end) => end <= s.from);
      if (col === -1) {
        col = colEnds.length;
        colEnds.push(s.to);
      } else {
        colEnds[col] = s.to;
      }
      s.col = col;
    }
    const cols = colEnds.length;

    for (const s of cluster) {
      // Widen right while the next column is free for this whole span.
      let span = 1;
      for (let c = s.col + 1; c < cols; c += 1) {
        const blocked = cluster.some(
          (o) => o !== s && o.col === c && o.from < s.to && o.to > s.from,
        );
        if (blocked) break;
        span += 1;
      }
      const height = ((s.to - s.from) / MINUTES) * 100;
      placed.push({
        event: s.event,
        top: (s.from / MINUTES) * 100,
        height: Math.max(height, MIN_HEIGHT_PCT),
        left: (s.col / cols) * 100,
        width: (span / cols) * 100,
        crowd: cols,
        clippedStart: s.clippedStart,
        clippedEnd: s.clippedEnd,
      });
    }

    i = j;
  }

  // Widest first so a narrow event drawn later sits ON its neighbour
  // rather than under it — the overlap has to be legible from the top.
  placed.sort((a, b) => b.width - a.width || a.left - b.left);

  return { timed: placed, allDay };
}

/** Where "now" sits in a day column, or null if it is another day. */
export function nowOffsetPct(day: Date, now = new Date()): number | null {
  const dayStart = startOfDay(day);
  if (now < dayStart || now >= addDays(dayStart, 1)) return null;
  return (minutesInto(now, dayStart) / MINUTES) * 100;
}

/** The seven days of the week `day` falls in, Monday first by default. */
export function weekDays(day: Date, weekStartsOn: 0 | 1 = 1): Date[] {
  const d = startOfDay(day);
  const shift = (d.getDay() - weekStartsOn + 7) % 7;
  const first = addDays(d, -shift);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
}

/* ───────────────────────── dragging ─────────────────────────────
 *
 * CAL-PRO-2. Moving and resizing is the second thing that tells you
 * a calendar is real, and the whole of it that can go wrong is the
 * arithmetic: which slot a pointer is over, where an event lands when
 * it gets there, and what stops a drag from making an event shorter
 * than a sliver or longer than a day.
 *
 * So the arithmetic lives here with the layout, pure and tested, and
 * the view above it only has to turn pixels into minutes and draw
 * what comes back. Nothing in this section touches the DOM, knows
 * about pointers, or persists anything.
 */

/**
 * The grid lines a drag lands on.
 *
 * Fifteen minutes because that is the interval people's own times are
 * already on — a 9:00, a 9:30, a 2:15 — and because at 56px an hour
 * it is a 14px step, which is about the smallest a hand can aim at.
 */
export const SNAP_MINUTES = 15;
/** Below this an event is a sliver you can neither read nor grab. */
export const MIN_EVENT_MINUTES = 15;

export interface Span {
  start: Date;
  end: Date;
}

/** Nearest grid line. */
export const snapMinutes = (m: number, step: number = SNAP_MINUTES): number =>
  Math.round(m / step) * step;

/** Minutes past midnight by the wall clock, unclamped. */
const clockMinutes = (d: Date): number =>
  d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;

/**
 * `minutes` past midnight on the day `on` falls in.
 *
 * Over 1440 or below 0 rolls the date, which is what makes an event
 * dragged past midnight land on tomorrow rather than clamp at 23:59.
 */
const atClock = (on: Date, minutes: number): Date => {
  const out = startOfDay(on);
  out.setMinutes(minutes);
  return out;
};

/**
 * The same clock time, `days` later.
 *
 * `setDate` and not 24h of milliseconds: dragging a 9am shoot from
 * the Saturday the clocks change to the Sunday has to leave it at
 * 9am, and adding 1440 minutes would leave it at 8 or 10.
 */
const shiftDays = (d: Date, days: number): Date => {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
};

/**
 * Move: the whole event slides, its duration untouched.
 *
 * The snap is applied to the RESULT, not to the delta — an event that
 * came in from Google at 9:07 and gets nudged half an hour lands on
 * 9:30, not on 9:37. Dragging something is how you tidy it up, and a
 * calendar that preserves the mess is one you cannot tidy with.
 */
export function resolveMove(origin: Span, deltaMinutes: number, deltaDays: number): Span {
  const durationMs = Math.max(
    MIN_EVENT_MINUTES * 60000,
    origin.end.getTime() - origin.start.getTime(),
  );
  const base = shiftDays(origin.start, deltaDays);
  const start = atClock(base, snapMinutes(clockMinutes(origin.start) + deltaMinutes));
  return { start, end: new Date(start.getTime() + durationMs) };
}

/**
 * Resize: one edge moves, the other stays exactly where it was.
 *
 * The floor is enforced against the FIXED edge rather than by
 * clamping the delta, so dragging the bottom of a 9–10 up past nine
 * stops at 9:15 instead of inverting the event or snapping it to
 * something that depends on how fast the pointer was going.
 */
export function resolveResize(origin: Span, edge: 'start' | 'end', deltaMinutes: number): Span {
  if (edge === 'end') {
    const end = atClock(origin.end, snapMinutes(clockMinutes(origin.end) + deltaMinutes));
    const floor = new Date(origin.start.getTime() + MIN_EVENT_MINUTES * 60000);
    return { start: origin.start, end: end < floor ? floor : end };
  }
  const start = atClock(origin.start, snapMinutes(clockMinutes(origin.start) + deltaMinutes));
  const ceiling = new Date(origin.end.getTime() - MIN_EVENT_MINUTES * 60000);
  return { start: start > ceiling ? ceiling : start, end: origin.end };
}

/**
 * Drag on empty space to draw a new event.
 *
 * Both arguments are minutes from the top of `day`: where the pointer
 * went down and where it is now. Either direction works — dragging
 * UP from three o'clock draws two-to-three rather than nothing, which
 * is the difference between a forgiving gesture and a fussy one.
 *
 * The START is held inside the day — at the latest, a quarter of an
 * hour before midnight. Snapping rounds to the NEAREST line, so a
 * press in the last few pixels of the column rounds to 1440, and
 * without this that would be a block starting at midnight TOMORROW:
 * in a week view, drawing in Monday's last slot would quietly produce
 * a Tuesday event, and the ghost would be a sliver below the bottom
 * of the grid where nobody can see what they are about to make.
 *
 * The END is left free to run past 1440, so an evening block can
 * finish in the small hours. In practice the view clamps the pointer
 * to the column, so a cross-midnight block is one you MOVED there or
 * typed in — but the function has no business inventing a limit the
 * caller didn't ask for.
 */
export function resolveCreate(day: Date, anchorMinutes: number, pointerMinutes: number): Span {
  const a = snapMinutes(Math.max(0, anchorMinutes));
  const b = snapMinutes(Math.max(0, pointerMinutes));
  const from = Math.min(Math.min(a, b), MINUTES - MIN_EVENT_MINUTES);
  const to = Math.max(Math.max(a, b), from + MIN_EVENT_MINUTES);
  return { start: atClock(day, from), end: atClock(day, to) };
}

/* ────────────────────── bookable hours ──────────────────────────
 *
 * CAL-PRO-3. A studio's availability rules have existed since the
 * scheduler shipped and the calendar has never drawn them, so the one
 * question a photographer's calendar should answer at a glance —
 * "when can somebody book me?" — was answered only on a settings
 * page. These turn the rules into bands of minutes, which the grid
 * dims the outside of.
 *
 * Here with the rest of the arithmetic, and pure, for the same reason
 * as everything above it: merging intervals is easy to write and
 * quietly easy to get wrong, and nothing in this environment can look
 * at the result.
 */

/** Minutes from midnight, half-open: `from` is in, `to` is out. */
export interface MinuteBand {
  from: number;
  to: number;
}

/** What `bookableBands` needs of an availability rule, and no more. */
export interface DayRule {
  /** 0 = Sunday, 6 = Saturday, as the table stores it. */
  day_of_week: number;
  /** "09:00", or "09:00:00" — Postgres `time` serialises both ways. */
  start_time: string;
  end_time: string;
}

/** "09:00" / "09:00:00" / "9:5" → minutes. NaN-safe. */
const parseClock = (value: string): number => {
  const [h, m] = String(value).split(':');
  const hours = Number(h);
  const mins = Number(m);
  if (!Number.isFinite(hours)) return 0;
  return hours * 60 + (Number.isFinite(mins) ? mins : 0);
};

/**
 * The hours a client can book on `day`, merged and in order.
 *
 * Rules are per weekday and there can be several — a studio who
 * shoots mornings and edits afternoons has two for a Tuesday — so
 * overlapping and touching bands are merged rather than drawn on top
 * of each other, which would show as a seam in the wash.
 *
 * An end at or before its start is read as MIDNIGHT: "09:00" to
 * "00:00" is a rule meaning the rest of the day, and 540 to 0 would
 * otherwise be an empty band that silently drops the rule.
 */
export function bookableBands(rules: DayRule[], day: Date): MinuteBand[] {
  const weekday = day.getDay();
  const bands = rules
    .filter((r) => r.day_of_week === weekday)
    .map((r) => {
      const from = Math.max(0, Math.min(MINUTES, parseClock(r.start_time)));
      const raw = Math.max(0, Math.min(MINUTES, parseClock(r.end_time)));
      return { from, to: raw <= from ? MINUTES : raw };
    })
    .filter((b) => b.to > b.from)
    .sort((a, b) => a.from - b.from);

  const merged: MinuteBand[] = [];
  for (const b of bands) {
    const last = merged[merged.length - 1];
    // `>=` and not `>`: 09:00–12:00 beside 12:00–17:00 is one band,
    // not two with a hairline between them.
    if (last && b.from <= last.to) last.to = Math.max(last.to, b.to);
    else merged.push({ ...b });
  }
  return merged;
}

/**
 * The gaps: everything in the day that `bands` does not cover.
 *
 * The grid shades these rather than painting the bookable hours,
 * because a calendar is mostly bookable hours and the quieter mark is
 * the one worth making.
 */
export function gapsAround(bands: MinuteBand[]): MinuteBand[] {
  const out: MinuteBand[] = [];
  let at = 0;
  for (const b of bands) {
    if (b.from > at) out.push({ from: at, to: b.from });
    at = Math.max(at, b.to);
  }
  if (at < MINUTES) out.push({ from: at, to: MINUTES });
  return out;
}
